import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';

/**
 * App-wide toast notifications. The console previously had no feedback
 * channel at all (window.confirm + silently swallowed mutation errors);
 * every admin-ops mutation reports through this instead.
 */

export type ToastKind = 'success' | 'error';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

type ToastAction = { type: 'add'; toast: ToastItem } | { type: 'remove'; id: number };

function toastReducer(state: ToastItem[], action: ToastAction): ToastItem[] {
  switch (action.type) {
    case 'add':
      return [...state, action.toast];
    case 'remove':
      return state.filter((toast) => toast.id !== action.id);
  }
}

interface ToastContextType {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const AUTO_DISMISS_MS = 5000;
let nextToastId = 1;

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const isError = toast.kind === 'error';
  return (
    <div
      role="status"
      className={`glass-card flex items-start space-x-3 p-4 w-80 animate-spring-in border-l-4 ${
        isError ? 'border-l-red-500' : 'border-l-green-500'
      }`}
    >
      {isError ? (
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      ) : (
        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
      )}
      <p className="flex-1 text-sm text-white break-words">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-gray-text hover:text-white flex-shrink-0"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, dispatch] = useReducer(toastReducer, []);

  const dismiss = useCallback((id: number) => dispatch({ type: 'remove', id }), []);

  const push = useCallback((kind: ToastKind, message: string) => {
    dispatch({ type: 'add', toast: { id: nextToastId++, kind, message } });
  }, []);

  const value = useMemo<ToastContextType>(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-[60] space-y-3">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
